import { useEffect, useRef, useState } from 'react';
import { SearchIcon } from './icons';

export type Command = {
  id: string;
  label: string;
  group: string;
  run: () => void;
};

/**
 * ⌘K command palette — searches pages, links, and actions. Open it from the
 * sidebar search trigger or Cmd/Ctrl+K. Arrow keys move, Enter runs, Esc closes.
 */
export function CommandPalette({
  open,
  commands,
  onClose,
}: {
  open: boolean;
  commands: Command[];
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? commands.filter((c) => `${c.label} ${c.group}`.toLowerCase().includes(needle))
    : commands;

  useEffect(() => {
    if (!open) return;
    setQ('');
    setSel(0);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    setSel(0);
  }, [q]);

  if (!open) return null;

  function activate(i: number) {
    const cmd = filtered[i];
    if (!cmd) return;
    onClose();
    cmd.run();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel((s) => Math.min(s + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel((s) => Math.max(s - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(sel);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  return (
    <div
      className="cmdk"
      role="dialog"
      aria-modal="true"
      aria-label="Command menu"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="cmdk__panel" onKeyDown={onKeyDown}>
        <div className="cmdk__searchrow">
          <SearchIcon />
          <input
            ref={inputRef}
            className="cmdk__input"
            placeholder="Search pages, links, actions…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search"
          />
          <kbd className="cmdk__esc">esc</kbd>
        </div>
        <div className="cmdk__list" role="listbox" aria-label="Results">
          {filtered.length === 0 && <div className="cmdk__empty">No results</div>}
          {filtered.map((c, i) => (
            <button
              key={c.id}
              type="button"
              role="option"
              aria-selected={i === sel}
              className={`cmdk__item${i === sel ? ' is-sel' : ''}`}
              onMouseMove={() => setSel(i)}
              onClick={() => activate(i)}
            >
              <span className="cmdk__label">{c.label}</span>
              <span className="cmdk__group">{c.group}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
